<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Loading
  </div>
  <div v-else class="pb-3">
    <b-row class="mb-2 pr-3">
      <b-col class="pr-1">
        <b-input-group prepend="Received">
          <b-form-datepicker v-model="filter_received_start_date">
            <template #button-content>
              <i class="fa fa-play" />
            </template>
          </b-form-datepicker>
          <b-form-datepicker
            v-model="filter_received_end_date"
            :min="filter_received_start_date"
          >
            <template #button-content>
              <i class="fa fa-pause" />
            </template>
          </b-form-datepicker>
        </b-input-group>
      </b-col>
      <b-col class="px-1">
        <b-input-group prepend="Created">
          <b-form-datepicker v-model="filter_created_start_date">
            <template #button-content>
              <i class="fa fa-play" />
            </template>
          </b-form-datepicker>
          <b-form-datepicker
            v-model="filter_created_end_date"
            :min="filter_created_start_date"
          >
            <template #button-content>
              <i class="fa fa-pause" />
            </template>
          </b-form-datepicker>
        </b-input-group>
      </b-col>
    </b-row>
    <b-row class="mb-2 pr-3">
      <b-col class="pr-1">
        <b-input-group prepend="Status">
          <b-form-select
            v-model="filter_selected_status"
            :options="filter.status_options"
          />
        </b-input-group>
      </b-col>
      <b-col class="px-1">
        <b-input-group prepend="RFID">
          <b-form-input v-model="filter_rfid" placeholder="RFID Filter..." />
        </b-input-group>
      </b-col>
      <b-col class="px-1">
        <b-input-group prepend="Customer">
          <b-form-input
            v-model="filter_client"
            placeholder="Customer Filter..."
          />
        </b-input-group>
      </b-col>
      <b-col cols="2" class="px-1">
        <b-button block @click="applyFilters('apply')">Apply filter</b-button>
      </b-col>
      <b-col cols="2" class="pr-0 pl-1">
        <b-button block @click="applyFilters('clear')">Clear filters</b-button>
      </b-col>
    </b-row>
    <b-row class="mb-2 pr-3">
      <b-col class="pr-1">
        <b-input-group prepend="In Schedule">
          <b-form-select
            v-model="filter_is_schedule_selected"
            :options="filter.is_schedule_options"
          />
        </b-input-group>
      </b-col>
      <b-col class="px-1">
        <toggle-batch-button
          :max="samples_on_page"
          :submitting="toggles.submitting_select"
          :side="toggles.select_from"
          :value="toggles.select_value"
          :options="toggles.select_options"
          :disable="disableToggleButton('select')"
          process="select"
          @applyToggle="applyToggle"
          @updateToggleSelector="updateToggleSelector"
          @updateToggleValue="updateToggleValue"
        />
      </b-col>
      <b-col class="pr-0 pl-1">
        <toggle-batch-button
          :max="samples_on_page"
          :submitting="toggles.submitting_remove"
          :side="toggles.remove_from"
          :value="toggles.remove_value"
          :options="toggles.remove_options"
          :disable="disableToggleButton('remove')"
          process="remove"
          @applyToggle="applyToggle"
          @updateToggleSelector="updateToggleSelector"
          @updateToggleValue="updateToggleValue"
        />
      </b-col>
    </b-row>
    <b-row class="mb-2 pr-3">
      <b-col class="pr-1">
        <b-button
          block
          @click="handleToggleAll('check')"
          :disabled="disableToggleAllButton()"
        >
          <template v-if="toggles.submitting_check_all">
            <i class="fal fa-spinner fa-spin" /> {{ " Update..." }}
          </template>
          <template v-else>
            <i class="fal fa-check-square" /> Select All
          </template>
        </b-button>
      </b-col>
      <b-col class="pr-0 pl-1">
        <b-button
          block
          @click="handleToggleAll('uncheck')"
          :disabled="disableToggleAllButton()"
        >
          <template v-if="toggles.submitting_uncheck_all">
            <i class="fal fa-spinner fa-spin" />{{ " Update..." }}
          </template>
          <template v-else> <i class="fal fa-square" /> Remove All </template>
        </b-button>
      </b-col>
    </b-row>
    <SampleCohortQueryBanner 
      :filteredListLength="filtered_samples.length" 
      :scheduleModel="schedule"
      :updateSampleLength="updated_samples.length"
    />
    <template>
      <div
        v-if="sample_list_loading"
        class="text-center mt-5 font-2xl text-muted"
      >
        <i class="fal fa-spinner fa-spin" /> Sample list loading...
      </div>
      <sample-cohort-selector
        v-else
        :schedule_id="schedule_id"
        :filtered_samples="filtered_samples"
        :sample_list_loading="sample_list_loading"
        @updateCurrentPage="updateCurrentPage"
        @updatePerPage="updatePerPage"
        @fetchSchedule="fetchSchedule"
        @updateSchedule="updateSchedule"
      />
    </template>
  </div>
</template>

<script>
import moment from "moment";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import SampleCohortSelector from "./components/SampleCohortSelector";
import ToggleBatchButton from "@/components/shared/form/ToggleBatchButton";
import SampleCohortQueryBanner from "./components/SampleCohortQueryBanner.vue";
import SampleCollection from "@/collections/lab/SampleCollection";
import { mapActions, mapGetters } from "vuex";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { getModel } from "@/shared/fetch-utils";

const ScheduleRepository = LabRepositoryFactory.get("schedule");

export default {
  name: "ScheduleSampleCohortForm",
  components: {
    BasicHeading,
    HeaderButtonGroup,
    SampleCohortSelector,
    ToggleBatchButton,
    SampleCohortQueryBanner,
  },
  computed: {
    ...mapGetters({
      schedule_form: "scheduleForm",
    }),
    selected_samples() {
      return this.schedule_form.samples;
    },
    filtered_samples() {
      return this.default_samples
        ? this.default_samples.models.filter((sample) => {
            return this.filter_is_schedule_selected
              ? this.filter_is_schedule_selected === "in-schedule"
                ? this.schedule.samples.find((item) => item.id === sample.id)
                : !this.schedule.samples.find((item) => item.id === sample.id)
              : true;
          })
        : null;
    },
    samples_on_page() {
      if (!this.filtered_samples) {
        return null;
      }

      if (!this.filtered_samples.length) {
        return 0;
      }

      if (
        this.table.per_page * this.table.current_page <=
        this.filtered_samples.length
      ) {
        return this.table.per_page;
      } else {
        return (
          this.filtered_samples.length -
          this.table.per_page * (this.table.current_page - 1)
        );
      }
    },
    filter_created_start_date: {
      get() {
        return this.filter.created_start;
      },
      set(value) {
        this.filter.created_start = value;
        value > this.filter.created_end && (this.filter.created_end = value);
      },
    },
    filter_created_end_date: {
      get() {
        return this.filter.created_end;
      },
      set(value) {
        this.filter.created_end = value;
      },
    },
    filter_received_start_date: {
      get() {
        return this.filter.received_start;
      },
      set(value) {
        this.filter.received_start = value;
        value > this.filter.received_end && (this.filter.received_end = value);
      },
    },
    filter_received_end_date: {
      get() {
        return this.filter.received_end;
      },
      set(value) {
        this.filter.received_end = value;
      },
    },
    filter_client: {
      get() {
        return this.filter.client;
      },
      set(value) {
        this.filter.client = value;
      },
    },
    filter_rfid: {
      get() {
        return this.filter.rfid;
      },
      set(value) {
        this.filter.rfid = value;
      },
    },
    filter_selected_status: {
      get() {
        return this.filter.status_selected;
      },
      set(value) {
        this.filter.status_selected = value;
      },
    },
    filter_is_schedule_selected: {
      get() {
        return this.filter.is_schedule_selected;
      },
      set(value) {
        this.filter.is_schedule_selected = value;
      },
    },
  },

  data() {
    return {
      loading: true,
      sample_list_loading: true,
      schedule: null,
      sample_type: null,
      default_samples: null,
      schedule_id: this.$route.params.schedule_id || null,
      filter: {
        received_start: moment().subtract(3, "d").format("YYYY-MM-DD"),
        received_end: moment().format("YYYY-MM-DD"),
        created_start: null,
        created_end: null,
        rfid: "",
        client: "",
        status_selected: "not-prep-scheduled",
        status_options: [
          { value: null, text: " - No State Filter" },
          { value: "not-prep-scheduled", text: "Not scheduled for prep" },
          { value: "prep-scheduled", text: "Scheduled for prep" },
          { value: "not-prepping", text: "Not Prepping" },
          { value: "prepping", text: "Prepping" },
          { value: "not-prepped", text: "Not Prepped" },
          { value: "prepped", text: "Prepped" },
          { value: "not-test-scheduled", text: "Not scheduled for testing" },
          { value: "test-scheduled", text: "Scheduled for testing" },
          { value: "not-testing", text: "Not Testing" },
          { value: "testing", text: "Testing" },
          { value: "not-tested", text: "Not Tested" },
          { value: "tested", text: "Tested" },
        ],
        is_schedule_selected: null,
        is_schedule_options: [
          { value: null, text: "All Samples" },
          { value: "not-in-schedule", text: "Samples Not In Schedule" },
          { value: "in-schedule", text: "Samples In Schedule" },
        ],
      },
      toggles: {
        select_options: [
          { text: "Select From Top", value: "top" },
          { text: "Select From Bottom", value: "bottom" },
        ],
        remove_options: [
          { text: "Remove From Top", value: "top" },
          { text: "Remove From Bottom", value: "bottom" },
        ],
        select_from: "top",
        remove_from: "top",
        select_value: 0,
        remove_value: 0,
        submitting_select: false,
        submitting_remove: false,
        submitting_check_all: false,
        submitting_uncheck_all: false,
      },
      table: {
        per_page: 500,
        current_page: 1,
      },
      updated_samples: [],
    };
  },
  methods: {
    ...mapActions(["UPDATE_SCHEDULE_FORM"]),
    updateCurrentPage(value) {
      this.table.current_page = value;
    },
    updatePerPage(value) {
      this.table.per_page = value;
    },
    updateToggleSelector({ process, value }) {
      this.toggles[`${process}_from`] = value;
    },
    updateToggleValue({ process, value }) {
      this.toggles[`${process}_value`] = value;
    },
    updateSamplesInSchedule(samples) {
      this.updated_samples = samples; 
    },
    toggleSingleSample(collection, toggled_sample, process) {
      const sampleCheck = !!this.selected_samples.hasByID(toggled_sample);

      if (process === "remove") {
        sampleCheck &&
          collection.remove((sample) => sample.id === toggled_sample.id);
      } else {
        !sampleCheck && collection.add(toggled_sample);
      }
    },
    updateSchedule(updated_samples) {
      this.updateSamplesInSchedule(updated_samples);
      this.schedule.samples = updated_samples;
    },
    disableToggleAllButton() {
      return !this.filtered_samples || !this.filtered_samples.length;
    },
    disableToggleButton(process) {
      return (
        this.disableToggleAllButton() ||
        !this.toggles[`${process}_value`] ||
        this.toggles[`${process}_value`] > this.samples_on_page
      );
    },
    async syncCohortSamples({ update, process, action }) {
      const sample_list = update.models.map((sample) => sample.id);

      try {
        await ScheduleRepository.syncSamples(this.schedule.id, sample_list);
        this.$bvToast.toast("Schedule sample list updated.", {
          title: "Update successful.",
          variant: "success",
        });

        this.fetchSchedule();

        this.UPDATE_SCHEDULE_FORM({ field: "samples", value: update });
      } catch ({ response }) {
        this.$bvToast.toast(response.data.error, {
          title: "Update Failed.",
          variant: "danger",
        });
      }

      process && (this.toggles[`submitting_${process}`] = false);
      action && (this.toggles[`submitting_${action}_all`] = false);
    },

    applyToggle(process, side) {
      this.toggles[`submitting_${process}`] = true;

      const update = this.selected_samples.models.reduce((acc, cur) => {
        acc.add(cur);

        return acc;
      }, new SampleCollection());
      const page_multiplier = this.table.current_page - 1;

      let counter = 0;

      while (this.toggles[`${process}_value`] > counter) {
        const top_border = this.table.per_page * page_multiplier + counter;
        const bottom_border =
          this.table.per_page * page_multiplier +
          this.samples_on_page -
          1 -
          counter;
        const toggled_sample =
          this.filtered_samples[side === "top" ? top_border : bottom_border];

        this.toggleSingleSample(update, toggled_sample, process);

        counter++;
      }

      this.updateSamplesInSchedule(update.models);
      this.syncCohortSamples({ update, process });
    },

    handleToggleAll(action) {
      this.toggles[`submitting_${action}_all`] = true;

      const update = new SampleCollection();

      this.selected_samples.each((sample) => {
        update.add(sample);
      });

      if (action === "check") {
        this.filtered_samples.forEach((item) => update.add(item));
        this.updateSamplesInSchedule(this.filtered_samples);
      } else {
        update.clear();
        this.updateSamplesInSchedule([]);
      }

      this.syncCohortSamples({ update, action });
    },

    applyFilters(action) {
      if (action === "clear") {
        this.filter.received_start = null;
        this.filter.received_end = null;
        this.filter.created_start = null;
        this.filter.created_end = null;
        this.filter.rfid = null;
        this.filter.client = null;
        this.filter.is_schedule_selected = null;
        this.filter.status_selected = "not-prep-scheduled";
      }

      this.fetchSamples();
    },

    async fetchSchedule() {
      this.schedule = await getModel({
        model_type: "schedule",
        id: this.schedule_id,
        message: this.$bvToast,
      });
    },

    async fetchSamples() {
      this.sample_list_loading = true;

      const collection = new SampleCollection();
      const filter = collection.getFilter();
      const connections = [
        "submission",
        "submission.sampleType",
        "submission.testPackage",
        "submission.turnaround",
        "submission.project",
        "submission.project.customer",
        "current_sample_status_log",
      ];
      filter.filterEquals(
        "submission.sample_type_id",
        this.schedule.sample_type_id
      );
      filter.filterNotNull("received_at");
      filter.filterNull("quality_control_id");
      filter.setLimit("all");
      filter.defaultSortAddAsc("receiving_id");

      connections.forEach((connection) => {
        filter.connectionAdd(connection);
      });

      this.filter.received_start &&
        filter.filterGreater('received_at', moment(`${this.filter.received_start} 00:00:00`).format('YYYY-MM-DDTHH:mm:ssZ ')) 
      this.filter.received_end &&
        filter.filterLess('received_at', moment(`${this.filter.received_end} 23:59:59`).format('YYYY-MM-DDTHH:mm:ssZ ')) 
      
      this.filter.created_start &&
        filter.filterGreater('created_at', moment(`${this.filter.created_start} 00:00:00`).format('YYYY-MM-DDTHH:mm:ssZ ')) 
      this.filter.created_end &&
        filter.filterLess('created_at', moment(`${this.filter.created_end} 23:59:59`).format('YYYY-MM-DDTHH:mm:ssZ ')) 

      // this.applyDateFilter(filter, "received");
      // this.applyDateFilter(filter, "created");
      this.applyStatusFilter(filter);
      this.applyRfidFilter(filter);
      this.applyClientFilter(filter);

      try {
        await collection.fetch();
        this.default_samples = collection;
        this.sample_list_loading = false;
      } catch ({ response }) {
        this.$bvToast.toast(response.data.error, {
          title: "Server Error.",
          variant: "danger",
        });
      }
    },
    async init() {
      await this.fetchSchedule();
      await this.fetchSamples();

      this.UPDATE_SCHEDULE_FORM({
        field: "samples",
        value: this.schedule.samples.reduce((acc, cur) => {
          acc.add(cur);

          return acc;
        }, new SampleCollection()),
      });
      this.loading = false;
    },
    applyDateFilter(filter, process) {
      this.filter[`${process}_start`] &&
        filter.filterGreater(
          `${process}_at`,
          moment(`${this.filter[`${process}_at`]} 00:00:00`).unix()
        );
      this.filter[`${process}_end`] &&
        filter.filterLess(
          `${process}_at`,
          moment(`${this.filter[`${process}_at`]} 23:59:59`).unix()
        );
    },
    applyStatusFilter(filter) {
      if (this.filter.status_selected) {
        switch (this.filter.status_selected) {
          case "not-prep-scheduled":
            filter.filterNull("prepping_scheduled_at");
            break;
          case "prep-scheduled":
            filter.filterNotNull("prepping_scheduled_at");
            break;
          case "not-prepping":
            filter.filterNull("prepping_at");
            break;
          case "prepping":
            filter.filterNullNull("prepping_at");
            break;
          case "not-prepped":
            filter.filterNull("prepped_at");
            break;
          case "prepped":
            filter.filterNotNull("prepped_at");
            break;
          case "not-test-scheduled":
            filter.filterNull("testing_scheduled_at");
            break;
          case "test-scheduled":
            filter.filterNotNull("testing_scheduled_at");
            break;
          case "not-testing":
            filter.filterNull("testing_at");
            break;
          case "testing":
            filter.filterNotNull("testing_at");
            break;
          case "not-tested":
            filter.filterNull("tested_at");
            break;
          case "tested":
            filter.filterNotNull("tested_at");
            break;
        }
      }
    },
    applyRfidFilter(filter) {
      this.filter.rfid &&
        filter.filterContains("receiving_id", this.filter.rfid);
    },
    applyClientFilter(filter) {
      this.filter.client &&
        filter.filterContains(
          "submission.project.customer.full_name",
          this.filter.client
        );
    },
  },
  mounted() {
    this.loading = true;

    this.init();
  },
};
</script>
